import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { storeClientBalance, getBalancesOnClient, exportClientBalanceHistory } from "@/Api/clients";
import { toast } from "sonner";
import { Mask } from "@/utils/mask";
import { Label } from "@/components/ui/label";
import { PlusCircle, MinusCircle, Eye, ExternalLink, X, FileText, ImageIcon, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Download, Loader } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth } from "@/hooks/auth";
import { TypeUser } from "@/types/userType";
import { Textarea } from "@/components/ui/textarea";

interface BalanceModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  clientUuid: string;
  clientId?: string;
  clientName: string;
  currentBalance?: number;
  clientHasPurchaseVoucher?: boolean;
}

interface BalanceFile {
  id: number;
  uuid: string;
  file: string;
  description?: string;
  created_by: number;
}

interface ContestInvestigationSale {
  id: number;
  uuid: string;
  sale_id: number | null;
  contest_product_id: number;
  product_classification_id: number;
  investigation_price_per_share: number;
  investigation_price_total: number;
  receive_by_cashier: boolean;
  non_winning_ticket: number;
  product_classification?: {
    id: number;
    uuid: string;
    name: string;
    lottery?: {
      id: number;
      name: string;
      color: string;
    };
  };
  contest_product_classification?: {
    id: number;
    product_name: string;
    contest_id: number;
    contest?: {
      id: number;
      name: string;
    };
    lottery?: {
      id: number;
      name: string;
      color: string;
    };
    product_classification?: {
      id: number;
      name: string;
    };
  };
}

interface BalanceTransaction {
  id: number;
  uuid: string;
  client_id: number;
  contest_investigation_id: number | null;
  balance: number;
  balance_flow_type_id: number;
  balance_origin_type_id: number;
  file?: string;
  files?: BalanceFile[];
  description?: string;
  is_receive_by_cashier?: boolean;
  prize_value?: number;
  client_quotas?: number;
  created_by: {
    id: number;
    name: string;
    email: string;
  };
  created_at: string;
  updated_at: string;
  balance_flow_type: {
    id: number;
    name: string;
  };
  balance_origin_type: {
    id: number;
    name: string;
  };
  client: {
    id: number;
    name: string;
    email: string;
    phone: string;
    addresses: Array<{
      id: number;
      street: string;
      number: string;
      complement: string | null;
      neighborhood: string;
      city: string;
      state: string;
      zip_code: string;
      latitude: string | null;
      longitude: string | null;
      pivot: {
        client_id: number;
        address_id: number;
        is_main: number;
        reference: string | null;
        created_at: string;
        updated_at: string;
      };
    }>;
  };
  contest_investigation_sale: ContestInvestigationSale | null;
}

export const BalanceModal = ({
  open,
  onClose,
  onSuccess,
  clientUuid,
  clientId,
  clientName,
  currentBalance = 0,
  clientHasPurchaseVoucher = false,
}: BalanceModalProps) => {
  const [balanceValue, setBalanceValue] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [isNegative, setIsNegative] = useState(false);
  const [hasPurchaseVoucher, setHasPurchaseVoucher] = useState(false);
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState<BalanceTransaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const { user } = useAuth();
  const isSellerUser = user?.user_type_id == TypeUser.seller;
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Estados do modal de detalhes da transação
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] =
    useState<BalanceTransaction | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [filePreviews, setFilePreviews] = useState<{ file: File; preview: string | null }[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (open && clientId) {
      setCurrentPage(1);
      fetchTransactions(1);
    }

    // Limpar campos quando o modal fecha
    if (!open) {
      setBalanceValue("");
      setDescription("");
      setFiles([]);
      setIsNegative(false);
      setHasPurchaseVoucher(false);
      setShowConfirmation(false);
      filePreviews.forEach((fp) => {
        if (fp.preview) URL.revokeObjectURL(fp.preview);
      });
      setFilePreviews([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [open, clientId]);

  const fetchTransactions = async (page: number = 1) => {
    try {
      setLoadingTransactions(true);
      const { data } = await getBalancesOnClient(clientId || "", page);
      if (data?.response) {
        setTransactions(data.response.data || []);
        setCurrentPage(data.response.current_page || 1);
        setLastPage(data.response.last_page || 1);
        setTotalTransactions(data.response.total || 0);
      }
    } catch (error) {
      console.error("Erro ao buscar transações:", error);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validações
    if (!balanceValue) {
      toast.error("Informe um valor para o ajuste");
      return;
    }

    if (!description || description.trim().length === 0) {
      toast.error("Informe uma descrição para o ajuste");
      return;
    }

    if (description.length > 1000) {
      toast.error("A descrição deve ter no máximo 1000 caracteres");
      return;
    }

    if (files.length === 0) {
      toast.error("Selecione pelo menos um arquivo (PDF, JPG, JPEG ou PNG)");
      return;
    }

    // Validar tipo de arquivo
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`Arquivo "${file.name}" inválido. Use PDF, JPG, JPEG ou PNG`);
        return;
      }
    }

    // Converter o valor para número para validação
    let value: number;
    try {
      value = parseFloat(Mask.transformCurrencyInFloat(balanceValue));
      if (isNaN(value) || value <= 0) {
        toast.error("Valor inválido");
        return;
      }
    } catch {
      toast.error("Valor inválido");
      return;
    }

    setShowConfirmation(true);
  };

  const handleConfirmedSubmit = async () => {
    try {
      setLoading(true);

      const value = parseFloat(Mask.transformCurrencyInFloat(balanceValue));
      const adjustedValue = isNegative ? -value : value;

      await storeClientBalance(
        clientUuid,
        adjustedValue,
        description,
        files,
        clientHasPurchaseVoucher ? hasPurchaseVoucher : false
      );
      toast.success(
        `Saldo ${isNegative ? "descontado" : "adicionado"} com sucesso!`
      );

      onSuccess();
      setCurrentPage(1);
      fetchTransactions(1);
      setBalanceValue("");
      setDescription("");
      setFiles([]);
      setIsNegative(false);
      setHasPurchaseVoucher(false);
      setShowConfirmation(false);
      filePreviews.forEach((fp) => {
        if (fp.preview) URL.revokeObjectURL(fp.preview);
      });
      setFilePreviews([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Erro ao ajustar saldo:", error);
      toast.error("Não foi possível ajustar o saldo do cliente");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const isImageFile = (url: string) => {
    return /\.(jpg|jpeg|png|gif|webp)/i.test(url);
  };

  const getFileNameFromUrl = (url: string) => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const fileName = pathname.split("/").pop() || "arquivo";
      return decodeURIComponent(fileName);
    } catch {
      const parts = url.split("/");
      return parts.pop() || "arquivo";
    }
  };

  const handleFilesChange = (selectedFiles: File[]) => {
    const newPreviews = selectedFiles.map((file) => {
      if (file.type.startsWith("image/")) {
        return { file, preview: URL.createObjectURL(file) };
      }
      return { file, preview: null };
    });
    setFilePreviews((prev) => [...prev, ...newPreviews]);
    setFiles((prev) => [...prev, ...selectedFiles]);
  };

  const handleRemoveFile = (index: number) => {
    const previewToRemove = filePreviews[index];
    if (previewToRemove?.preview) {
      URL.revokeObjectURL(previewToRemove.preview);
    }
    setFilePreviews((prev) => prev.filter((_, i) => i !== index));
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleViewDetails = (transaction: BalanceTransaction) => {
    setSelectedTransaction(transaction);
    setDetailsModalOpen(true);
  };

  const handleExportHistory = async () => {
    if (!clientId) {
      toast.error("ID do cliente não encontrado");
      return;
    }

    setIsExporting(true);
    try {
      const blob = await exportClientBalanceHistory(clientId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeName = clientName.replace(/\s+/g, "-").toLowerCase();
      a.download = `historico-transacoes-${safeName}-${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Histórico exportado com sucesso!");
    } catch (error) {
      console.error("Erro ao exportar histórico:", error);
      toast.error("Erro ao exportar histórico de transações");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        {!isSellerUser && !showConfirmation && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <h2 className="text-xl font-bold">Ajustar Saldo do Cliente</h2>
            <p className="text-sm text-gray-500">
              Cliente: <span className="font-medium">{clientName}</span>
            </p>
            {currentBalance !== undefined && (
              <p className="text-sm text-gray-500">
                Saldo atual:{" "}
                <span className="font-medium">
                  {Mask.formatCurrency(currentBalance)}
                </span>
              </p>
            )}

            <div className="space-y-2">
              <Label htmlFor="balance-type">Tipo de Ajuste</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={isNegative ? "outline" : "default"}
                  className={
                    !isNegative ? "bg-green-600 hover:bg-green-700" : ""
                  }
                  onClick={() => setIsNegative(false)}
                >
                  <PlusCircle className="mr-2 h-4 w-4" />
                  Adicionar
                </Button>
                <Button
                  type="button"
                  variant={isNegative ? "default" : "outline"}
                  className={isNegative ? "bg-red-600 hover:bg-red-700" : ""}
                  onClick={() => setIsNegative(true)}
                >
                  <MinusCircle className="mr-2 h-4 w-4" />
                  Subtrair
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="balance-value">Valor</Label>
              <Input
                id="balance-value"
                typeMask="currency"
                value={balanceValue}
                onChange={(e) => setBalanceValue(e.target.value)}
                placeholder="R$ 0,00"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">
                Descrição <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descreva o motivo do ajuste de saldo..."
                maxLength={1000}
                rows={4}
                className="resize-none"
              />
              <p className="text-xs text-gray-500">
                {description.length}/1000 caracteres
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="file">
                Arquivos (PDF, JPG, JPEG ou PNG){" "}
                <span className="text-red-500">*</span>
              </Label>
              <input
                ref={fileInputRef}
                id="file"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                multiple
                onChange={(e) => {
                  const selectedFiles = Array.from(e.target.files || []);
                  if (selectedFiles.length > 0) {
                    handleFilesChange(selectedFiles);
                  }
                  if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                  }
                }}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
              />
              {filePreviews.length > 0 && (
                <div className="space-y-2 mt-2">
                  <p className="text-xs text-gray-500">{filePreviews.length} arquivo(s) selecionado(s):</p>
                  {filePreviews.map((fp, index) => (
                    <div key={index} className="border rounded-md p-2 bg-gray-50 dark:bg-gray-800">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {fp.file.type === "application/pdf" ? (
                            <FileText className="h-4 w-4 text-red-500 shrink-0" />
                          ) : (
                            <ImageIcon className="h-4 w-4 text-blue-500 shrink-0" />
                          )}
                          <span className="text-xs text-green-600 truncate">{fp.file.name}</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 shrink-0"
                          onClick={() => handleRemoveFile(index)}
                        >
                          <X className="h-3 w-3 text-gray-700 dark:text-gray-200" />
                        </Button>
                      </div>
                      {fp.preview && (
                        <img
                          src={fp.preview}
                          alt={fp.file.name}
                          className="w-full h-auto max-h-32 object-contain rounded border"
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* {clientHasPurchaseVoucher && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="has-purchase-voucher"
                  checked={hasPurchaseVoucher}
                  onCheckedChange={(checked) =>
                    setHasPurchaseVoucher(checked === true)
                  }
                />
                <Label
                  htmlFor="has-purchase-voucher"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Tipo Vale Compra
                </Label>
              </div>
            )} */}

            <Button
              type="submit"
              className="w-full"
              disabled={
                loading || !balanceValue || !description.trim() || filePreviews.length === 0
              }
            >
              Continuar
            </Button>
          </form>
        )}

        {showConfirmation && !isSellerUser && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold">Confirmar Ajuste de Saldo</h2>
            <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-800 space-y-3">
              <div>
                <span className="text-sm text-gray-500">Cliente:</span>
                <p className="font-medium">{clientName}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Tipo de Ajuste:</span>
                <p className={`font-medium ${isNegative ? "text-red-600" : "text-green-600"}`}>
                  {isNegative ? "Subtração" : "Adição"}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Valor:</span>
                <p className={`font-bold text-lg ${isNegative ? "text-red-600" : "text-green-600"}`}>
                  {isNegative ? "-" : "+"}{balanceValue}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Descrição:</span>
                <p className="text-sm whitespace-pre-wrap">{description}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Arquivos ({filePreviews.length}):</span>
                <div className="mt-1 space-y-2">
                  {filePreviews.map((fp, index) => (
                    <div key={index} className="border rounded p-2 bg-white dark:bg-gray-900">
                      <div className="flex items-center gap-2">
                        {fp.file.type === "application/pdf" ? (
                          <FileText className="h-4 w-4 text-red-500" />
                        ) : (
                          <ImageIcon className="h-4 w-4 text-blue-500" />
                        )}
                        <span className="text-xs truncate">{fp.file.name}</span>
                      </div>
                      {fp.preview && (
                        <img
                          src={fp.preview}
                          alt={fp.file.name}
                          className="w-full h-auto max-h-24 object-contain rounded mt-2"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setShowConfirmation(false)}
                disabled={loading}
              >
                Voltar
              </Button>
              <Button
                type="button"
                className="flex-1"
                onClick={handleConfirmedSubmit}
                disabled={loading}
              >
                {loading ? "Processando..." : "Confirmar Ajuste"}
              </Button>
            </div>
          </div>
        )}

        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold">
              Histórico de Transações
            </h3>
            {transactions.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportHistory}
                disabled={isExporting}
                className="gap-2"
              >
                {isExporting ? (
                  <Loader className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Exportar
              </Button>
            )}
          </div>
          {loadingTransactions ? (
            <p className="text-center py-4 text-gray-500">
              Carregando transações...
            </p>
          ) : transactions.length > 0 ? (
            <ScrollArea className="h-[200px] rounded-md border p-2">
              <div className="space-y-3">
                {transactions.map((transaction) => (
                  <div
                    key={transaction.id}
                    className="border-b pb-2 last:border-0"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex justify-between items-center">
                          <span className="font-medium">
                            {transaction.balance_origin_type.name}
                          </span>

                          {(transaction.file || (transaction.files && transaction.files.length > 0) || transaction.description || (transaction.balance_origin_type_id === 1 && transaction.contest_investigation_sale)) && (
                            <Button
                              size="icon"
                              className="h-8 w-8 ml-2"
                              onClick={() => handleViewDetails(transaction)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                          <span
                            className={
                              transaction.is_receive_by_cashier
                                ? "text-blue-600 font-semibold"
                                : transaction.balance_flow_type_id === 1
                                ? "text-green-600"
                                : "text-red-600"
                            }
                          >
                            {transaction.balance_flow_type_id === 1 ? "+ " : ""}
                            {Mask.formatCurrency(
                              transaction.is_receive_by_cashier
                                ? transaction.prize_value || 0
                                : transaction.balance
                            )}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 flex justify-between mt-1">
                          <span>{formatDate(transaction.created_at)}</span>
                          <span>{transaction?.balance_flow_type?.name}</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          <span>
                            Criado por: {transaction?.created_by?.name}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          ) : (
            <p className="text-center py-4 text-gray-500">
              Nenhuma transação encontrada
            </p>
          )}

          {/* Controles de Paginação */}
          {lastPage > 1 && (
            <div className="flex items-center justify-between mt-3 pt-3 border-t">
              <span className="text-xs text-gray-500">
                {totalTransactions} transação(ões)
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchTransactions(1)}
                  disabled={currentPage <= 1 || loadingTransactions}
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchTransactions(currentPage - 1)}
                  disabled={currentPage <= 1 || loadingTransactions}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm px-2">
                  {currentPage} / {lastPage}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchTransactions(currentPage + 1)}
                  disabled={currentPage >= lastPage || loadingTransactions}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchTransactions(lastPage)}
                  disabled={currentPage >= lastPage || loadingTransactions}
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>

      {/* Modal de Detalhes da Transação */}
      <Dialog open={detailsModalOpen} onOpenChange={setDetailsModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="space-y-4">
            <h3 className="text-xl font-bold">Detalhes da Transação</h3>

            {selectedTransaction && (
              <>
                {/* Informações da Transação */}
                <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <span className="text-sm text-gray-500">Tipo:</span>
                      <p className="font-medium">
                        {selectedTransaction.balance_origin_type.name}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Valor:</span>
                      <p
                        className={`font-bold ${
                          selectedTransaction.is_receive_by_cashier
                            ? "text-blue-600"
                            : selectedTransaction.balance_flow_type_id === 1
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {selectedTransaction.balance_flow_type_id === 1
                          ? "+ "
                          : ""}
                        {Mask.formatCurrency(
                          selectedTransaction.is_receive_by_cashier
                            ? selectedTransaction.prize_value || 0
                            : selectedTransaction.balance
                        )}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Data:</span>
                      <p className="font-medium">
                        {formatDate(selectedTransaction.created_at)}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Criado por:</span>
                      <p className="font-medium">
                        {selectedTransaction.created_by.name}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Descrição */}
                {selectedTransaction.description && (
                  <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
                    <h4 className="text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                      Descrição
                    </h4>
                    <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                      {selectedTransaction.description}
                    </p>
                  </div>
                )}

                {/* Detalhes da Premiação - quando é uma premiação com dados de investigação */}
                {selectedTransaction.balance_origin_type_id === 1 && selectedTransaction.contest_investigation_sale && (
                  <div className="border rounded-lg overflow-hidden">
                    <div
                      className="px-4 py-2 text-white font-semibold"
                      style={{
                        backgroundColor:
                          selectedTransaction.contest_investigation_sale.contest_product_classification?.lottery?.color ||
                          selectedTransaction.contest_investigation_sale.product_classification?.lottery?.color ||
                          "#023859"
                      }}
                    >
                      Detalhes da Premiação
                    </div>
                    <div className="p-4 bg-gray-50 dark:bg-gray-800 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <span className="text-sm text-gray-500">Loteria:</span>
                          <p className="font-medium">
                            {selectedTransaction.contest_investigation_sale.contest_product_classification?.lottery?.name ||
                              selectedTransaction.contest_investigation_sale.product_classification?.lottery?.name ||
                              "-"}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500">Concurso:</span>
                          <p className="font-medium">
                            {selectedTransaction.contest_investigation_sale.contest_product_classification?.contest?.name || "-"}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500">Produto:</span>
                          <p className="font-medium">
                            {selectedTransaction.contest_investigation_sale.contest_product_classification?.product_name || "-"} -{" "}
                            {selectedTransaction.contest_investigation_sale.product_classification?.name || "-"}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500">Cotas do Cliente:</span>
                          <p className="font-medium">{selectedTransaction.client_quotas || "-"}</p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500">Valor Total Apurado:</span>
                          <p className="font-medium text-green-600">
                            {Mask.formatCurrency(selectedTransaction.contest_investigation_sale.investigation_price_total || 0)}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500">Valor por Cota:</span>
                          <p className="font-medium">
                            {Mask.formatCurrency(selectedTransaction.contest_investigation_sale.investigation_price_per_share || 0)}
                          </p>
                        </div>
                      </div>
                      <div className="pt-2 border-t">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Valor da Premiação do Cliente:
                          </span>
                          <span className="text-lg font-bold text-blue-600">
                            {Mask.formatCurrency(selectedTransaction.prize_value || 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Arquivos (novo formato) */}
                {selectedTransaction.files && selectedTransaction.files.length > 0 && (
                  <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                      Arquivos Anexados ({selectedTransaction.files.length})
                    </h4>
                    <div className="space-y-3">
                      {selectedTransaction.files.map((balanceFile, index) => (
                        <div key={balanceFile.id || index} className="border rounded-md p-3 bg-white dark:bg-gray-900">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-xs text-gray-500">Arquivo {index + 1}</span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(balanceFile.file, "_blank")}
                              className="gap-2"
                            >
                              <ExternalLink className="h-4 w-4" />
                              Abrir
                            </Button>
                          </div>
                          {isImageFile(balanceFile.file) ? (
                            <img
                              src={balanceFile.file}
                              alt={`Comprovante ${index + 1}`}
                              className="w-full h-auto rounded-md border max-h-48 object-contain"
                              onError={(e) => {
                                e.currentTarget.src = "";
                                e.currentTarget.alt = "Erro ao carregar imagem";
                              }}
                            />
                          ) : (
                            <div className="flex items-center gap-2 p-2 bg-gray-100 dark:bg-gray-800 rounded">
                              <FileText className="h-5 w-5 text-red-500" />
                              <span className="text-sm truncate" title={getFileNameFromUrl(balanceFile.file)}>
                                {getFileNameFromUrl(balanceFile.file)}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Arquivo (formato antigo - compatibilidade) */}
                {selectedTransaction.file && (!selectedTransaction.files || selectedTransaction.files.length === 0) && (
                  <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Arquivo Anexado
                      </h4>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          window.open(selectedTransaction.file, "_blank")
                        }
                        className="gap-2"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Abrir em nova aba
                      </Button>
                    </div>

                    {isImageFile(selectedTransaction.file) ? (
                      <div className="mt-2">
                        <img
                          src={selectedTransaction.file}
                          alt="Comprovante"
                          className="w-full h-auto rounded-md border"
                          onError={(e) => {
                            e.currentTarget.src = "";
                            e.currentTarget.alt = "Erro ao carregar imagem";
                          }}
                        />
                      </div>
                    ) : (
                      <div className="mt-2 flex items-center justify-center p-8 border-2 border-dashed rounded-md">
                        <div className="text-center">
                          <div className="flex items-center justify-center gap-2 mb-2">
                            <FileText className="h-5 w-5 text-red-500" />
                            <p className="text-sm text-gray-500" title={getFileNameFromUrl(selectedTransaction.file)}>
                              {getFileNameFromUrl(selectedTransaction.file)}
                            </p>
                          </div>
                          <Button
                            variant="default"
                            onClick={() =>
                              window.open(selectedTransaction.file, "_blank")
                            }
                            className="gap-2"
                          >
                            <ExternalLink className="h-4 w-4" />
                            Visualizar PDF
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};
